import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Skeleton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  LinearProgress
} from '@mui/material'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts'
import { formatCurrency, formatRUT } from '../../utils/formatters'

const TopDebtorsChart = ({ data, loading, title = "Top Clientes Deudores" }) => {
  const theme = useTheme()

  const formatTooltip = (value, name, props) => {
    if (name === 'totalDebt') {
      return [
        formatCurrency(value), 
        `Deuda Total - ${props.payload.pendingInvoices} facturas`
      ]
    }
    return [value, name]
  }

  const formatXAxisLabel = (tickItem) => {
    // Truncate long names
    return tickItem.length > 12 ? tickItem.substring(0, 12) + '...' : tickItem
  }

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Skeleton variant="rectangular" height={300} />
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Box 
            sx={{ 
              height: 300, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Typography variant="h4">🎉</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              ¡Excelente! No hay clientes con deudas pendientes
            </Typography>
          </Box>
        </CardContent>
      </Card>
    )
  }

  const maxDebt = Math.max(...data.map(item => item.totalDebt))

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        {data.length <= 5 ? (
          // List view for few items
          <List>
            {data.map((debtor, index) => (
              <ListItem key={debtor.id} sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: index === 0 ? 'error.main' : 
                               index === 1 ? 'warning.main' : 
                               'primary.main',
                      width: 40,
                      height: 40,
                      fontSize: '0.9rem'
                    }}
                  >
                    {debtor.name.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${debtor.name} - ${formatCurrency(debtor.totalDebt)}`}
                  secondary={`${formatRUT(debtor.rut)} • ${debtor.pendingInvoices} facturas`}
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: 600
                  }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    color: "text.secondary"
                  }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          // Bar chart for many items
          <Box sx={{ width: '100%', height: 300, mt: 2 }}>
            <ResponsiveContainer>
              <BarChart 
                data={data.slice(0, 10)} // Show top 10
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke={theme.palette.divider}
                />
                <XAxis 
                  dataKey="name" 
                  tickFormatter={formatXAxisLabel}
                  stroke={theme.palette.text.secondary}
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  tickFormatter={(value) => formatCurrency(value).replace('$', '$')}
                  stroke={theme.palette.text.secondary}
                  fontSize={12}
                />
                <Tooltip 
                  formatter={formatTooltip}
                  labelFormatter={(label) => `Cliente: ${label}`}
                  contentStyle={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: theme.shape.borderRadius
                  }}
                />
                <Bar 
                  dataKey="totalDebt" 
                  fill={theme.palette.error.main}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

export default TopDebtorsChart
